"use client"

import { useEffect } from "react"
import { useIntercomStore } from "@/stores/use-intercom-store"
import * as Sentry from "@sentry/nextjs"

import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { useSupportChatSettings } from "@/hooks/use-show-intercom"

const MAX_LOAD_ATTEMPTS = 3
const BOOT_TIMEOUT_MS = 15000

// Shrinks the Intercom home-screen banner. Hides the "Hey Max 👋" personalized title and
// collapses the (empty, after admin removed the logo) logo slot so the banner is compact.
// Only applied on the home view. The hashed class selector (intercom-jeinrp) targets
// Intercom's CSS-in-JS bundle and may need updating if Intercom changes their widget.
const MESSENGER_HEADER_CSS = `
  [data-testid="spaces-home"] [data-testid="title"] { display: none !important; }
  [data-testid="spaces-home"] [class*="intercom-jeinrp"] {
    height: 0 !important;
    margin: 0 !important;
    min-height: 0 !important;
  }
  [data-testid="spaces-home"] [class*="e2j9h0w6"] {
    padding: 20px 0 !important;
  }
  [data-testid="spaces-home"] [class*="e1ch7n1d2"] {
    margin-top: 24px !important;
  }
  [data-testid="close-button"] {
    top: 20px !important;
  }
`

type IntercomSettings = {
  app_id: string
  user_id?: string
  name?: string
  email?: string
  intercom_user_jwt?: string | null
  hide_default_launcher?: boolean
  company?: {
    company_id: string
    name?: string
  }
}

type IntercomFn = {
  (command: "boot", settings: IntercomSettings): void
  (command: "shutdown"): void
  (command: "show"): void
  (command: "hide"): void
  (command: "onUnreadCountChange", handler: (count: number) => void): void
  (command: "onHide", handler: () => void): void
  (command: "onShow", handler: () => void): void
}

declare global {
  interface Window {
    Intercom?: IntercomFn
  }
}

export function IntercomProvider({ children }: { children: React.ReactNode }) {
  const { data: user } = useFetchCurrentUser()
  const { redirectToDocs, showIntercom } = useSupportChatSettings()
  const setStatus = useIntercomStore((state) => state.setStatus)

  useEffect(() => {
    const appId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID

    if (!showIntercom || redirectToDocs) return

    if (!appId) {
      setStatus("error")
      Sentry.captureMessage("Intercom widget not configured: missing NEXT_PUBLIC_INTERCOM_APP_ID", {
        level: "error",
        tags: { feature: "intercom-launcher" },
      })
      return
    }

    if (!user) return

    setStatus("loading")

    let currentScript: HTMLScriptElement | null = null
    let settled = false

    const fail = (reason: string, attempts: number) => {
      if (settled) return
      settled = true
      setStatus("error")
      Sentry.captureMessage(`Intercom widget failed to load: ${reason}`, {
        level: "error",
        tags: { feature: "intercom-launcher" },
        extra: { appId, attempts },
      })
    }

    const boot = () => {
      window.Intercom?.("boot", {
        app_id: appId,
        user_id: user.intercom_user_id,
        email: user.email,
        name: user.full_name,
        intercom_user_jwt: user.intercom_user_jwt,
        hide_default_launcher: true,
        company: {
          company_id: user.intercom_company_id,
          name: user.company?.name,
        },
      })
      settled = true
      setStatus("ready")
    }

    const loadScript = (attempt: number) => {
      const script = document.createElement("script")
      currentScript = script
      script.src = `https://widget.intercom.io/widget/${appId}`
      script.async = true
      script.onload = () => {
        if (window.Intercom) {
          boot()
        } else {
          retryOrFail("intercom-undefined-after-load", attempt)
        }
      }
      script.onerror = () => retryOrFail("script-onerror", attempt)
      document.head.appendChild(script)
    }

    const retryOrFail = (reason: string, attempt: number) => {
      currentScript?.remove()
      currentScript = null
      if (attempt < MAX_LOAD_ATTEMPTS) {
        loadScript(attempt + 1)
      } else {
        fail(reason, attempt)
      }
    }

    loadScript(1)

    const timeoutId = window.setTimeout(() => fail("boot-timeout", MAX_LOAD_ATTEMPTS), BOOT_TIMEOUT_MS)

    const injectHeaderStyles = () => {
      const iframe = document.querySelector<HTMLIFrameElement>(".intercom-messenger-frame iframe")
      const doc = iframe?.contentDocument
      if (!doc?.head) return false
      if (doc.getElementById("slant-intercom-header")) return true
      const style = doc.createElement("style")
      style.id = "slant-intercom-header"
      style.textContent = MESSENGER_HEADER_CSS
      doc.head.appendChild(style)
      return true
    }
    const styleInterval = window.setInterval(injectHeaderStyles, 300)

    return () => {
      window.clearInterval(styleInterval)
      window.clearTimeout(timeoutId)
      window.Intercom?.("shutdown")
      currentScript?.remove()
      setStatus("idle")
    }
  }, [user, redirectToDocs, showIntercom, setStatus])

  return <>{children}</>
}
